import { Timestamp } from 'firebase/firestore';
import { getFactionStoredWrestlers } from './factionWrestlers';
import {
  MAIN_EVENTER_COOLDOWN_DAYS,
  MAIN_EVENTER_DURATION_DAYS,
  addDays,
  getMainEventerCooldownDays,
  getMainEventerDurationDays,
  normalizeMainEventerCooldowns,
  serializeMainEventerCooldowns,
  startOfDay,
  toDate,
  withMainEventerCooldown,
} from './mainEventerCooldown';

export {
  MAIN_EVENTER_COOLDOWN_DAYS,
  MAIN_EVENTER_DURATION_DAYS,
  getMainEventerCooldownDays,
  getMainEventerDurationDays,
};

const normalizeLineupSnapshot = (snapshot) => {
  if (!snapshot) return null;

  const champion = snapshot.champion || snapshot.championId || null;
  const inRing = [...(snapshot.inRing || snapshot.inRingIds || [])].filter(Boolean);
  const backstage = [...(snapshot.backstage || snapshot.backstageIds || [])].filter(Boolean);
  const hasAssignedWrestlers =
    Boolean(champion) || inRing.length > 0 || backstage.length > 0;

  if (!hasAssignedWrestlers) return null;

  return {
    champion,
    inRing,
    backstage,
    mainEventerAssignedAt: toDate(
      snapshot.mainEventerAssignedAt ||
        snapshot.currentMainEventerAssignedAt ||
        snapshot.effectiveFrom
    ),
    mainEventerExpiresAt: toDate(snapshot.mainEventerExpiresAt),
    effectiveFrom: toDate(snapshot.effectiveFrom),
  };
};

const buildFactionLineupSnapshots = (faction) => {
  const history = Array.isArray(faction?.lineupHistory)
    ? faction.lineupHistory.map(normalizeLineupSnapshot).filter(Boolean)
    : [];
  const currentLineup = normalizeLineupSnapshot(faction?.currentLineup);
  const snapshots = [...history];

  if (currentLineup?.effectiveFrom) {
    const alreadyInHistory = snapshots.some(
      (snapshot) =>
        snapshot.effectiveFrom?.getTime() === currentLineup.effectiveFrom.getTime()
    );

    if (!alreadyInHistory) {
      snapshots.push(currentLineup);
    }
  } else if (currentLineup) {
    snapshots.push(currentLineup);
  }

  return snapshots.sort(
    (a, b) => (a.effectiveFrom?.getTime() || 0) - (b.effectiveFrom?.getTime() || 0)
  );
};

export const getFactionMainEventerCooldowns = (
  faction,
  referenceDate = new Date(),
  configSource = null
) => {
  const targetDate = toDate(referenceDate) || new Date();
  const cooldownDays = getMainEventerCooldownDays(configSource || faction);
  const snapshots = buildFactionLineupSnapshots(faction);
  const cooldowns = {};

  for (let index = 0; index < snapshots.length; index += 1) {
    const runStart = snapshots[index];
    const champion = runStart?.champion || null;

    if (!champion) continue;
    if (index > 0 && snapshots[index - 1]?.champion === champion) continue;

    let runEndIndex = index;
    while (
      runEndIndex + 1 < snapshots.length &&
      snapshots[runEndIndex + 1]?.champion === champion
    ) {
      runEndIndex += 1;
    }

    const runSnapshot = snapshots[runEndIndex];
    const expiresAt = runSnapshot?.mainEventerExpiresAt || null;
    const nextSnapshotStart = snapshots[runEndIndex + 1]?.effectiveFrom || null;

    let cooldownStart = null;

    if (expiresAt && nextSnapshotStart) {
      cooldownStart =
        nextSnapshotStart.getTime() < expiresAt.getTime()
          ? nextSnapshotStart
          : expiresAt;
    } else if (nextSnapshotStart) {
      cooldownStart = nextSnapshotStart;
    } else if (expiresAt && expiresAt.getTime() <= targetDate.getTime()) {
      cooldownStart = expiresAt;
    }

    if (!cooldownStart || cooldownStart.getTime() > targetDate.getTime()) {
      continue;
    }

    const cooldownUntil = addDays(
      startOfDay(cooldownStart),
      cooldownDays
    );

    if (cooldownUntil.getTime() <= targetDate.getTime()) {
      continue;
    }

    if (
      !cooldowns[champion] ||
      cooldownUntil.getTime() > cooldowns[champion].getTime()
    ) {
      cooldowns[champion] = cooldownUntil;
    }
  }

  return cooldowns;
};

export const getFactionMainEventerCooldownUntil = (
  faction,
  wrestlerId,
  referenceDate = new Date(),
  configSource = null
) => {
  if (!wrestlerId) return null;

  return (
    getFactionMainEventerCooldowns(
      faction,
      referenceDate,
      configSource
    )[wrestlerId] || null
  );
};

const buildLineupFromStatuses = (wrestlers = []) => {
  const champion =
    wrestlers.find((member) => member?.status === 'Main_Eventer')?.id || null;

  return {
    champion,
    inRing: wrestlers
      .filter((member) => member?.status === 'Main_Eventer' || member?.status === 'In_Ring')
      .map((member) => member.id),
    backstage: wrestlers
      .filter((member) => member?.status === 'Backstage')
      .map((member) => member.id),
  };
};

const replaceIdsInList = (list = [], replacements = {}) =>
  list
    .map((id) => replacements[id] || id)
    .filter(Boolean);

export const buildSwapReplacementMap = (wrestlerOutIds = [], wrestlerInIds = []) =>
  wrestlerOutIds.reduce((acc, wrestlerOutId, index) => {
    const wrestlerInId = wrestlerInIds[index];
    if (wrestlerOutId && wrestlerInId) {
      acc[wrestlerOutId] = wrestlerInId;
    }
    return acc;
  }, {});

export const buildStoredLineupSnapshotFromFaction = (
  faction,
  effectiveFrom = new Date(0)
) => {
  const storedWrestlers = getFactionStoredWrestlers(faction);
  const statusLineup = buildLineupFromStatuses(storedWrestlers);

  return {
    champion: statusLineup.champion,
    inRing: statusLineup.inRing,
    backstage: statusLineup.backstage,
    mainEventerAssignedAt: null,
    mainEventerExpiresAt: null,
    effectiveFrom: Timestamp.fromDate(effectiveFrom),
  };
};

const buildStoredLineupSnapshotFromSnapshot = (snapshot) => {
  const normalized = normalizeLineupSnapshot(snapshot);
  if (!normalized?.effectiveFrom) return null;

  return {
    champion: normalized.champion,
    inRing: normalized.inRing,
    backstage: normalized.backstage,
    mainEventerAssignedAt: normalized.mainEventerAssignedAt
      ? Timestamp.fromDate(normalized.mainEventerAssignedAt)
      : null,
    mainEventerExpiresAt: normalized.mainEventerExpiresAt
      ? Timestamp.fromDate(startOfDay(normalized.mainEventerExpiresAt))
      : null,
    effectiveFrom: Timestamp.fromDate(normalized.effectiveFrom),
  };
};

const appendUniqueLineupSnapshot = (history = [], snapshot = null) => {
  if (!snapshot) return history;

  const normalizedSnapshot = normalizeLineupSnapshot(snapshot);
  if (!normalizedSnapshot?.effectiveFrom) return history;

  const alreadyExists = history.some((historySnapshot) => {
    const normalizedHistorySnapshot = normalizeLineupSnapshot(historySnapshot);
    return (
      normalizedHistorySnapshot?.effectiveFrom &&
      normalizedHistorySnapshot.effectiveFrom.getTime() ===
        normalizedSnapshot.effectiveFrom.getTime()
    );
  });

  if (alreadyExists) return history;

  const storedSnapshot = buildStoredLineupSnapshotFromSnapshot(normalizedSnapshot);
  return storedSnapshot ? [...history, storedSnapshot] : history;
};

export const buildFactionLineupUpdateAfterSwap = ({
  faction,
  updatedWrestlers = [],
  replacements = {},
  now = new Date(),
  preferPreviousChampion = true,
  roster = null,
}) => {
  if (!faction || !Array.isArray(updatedWrestlers) || updatedWrestlers.length === 0) {
    return null;
  }

  const shouldPersistLineup =
    Boolean(faction.currentLineup || faction.lineup) ||
    (Array.isArray(faction.lineupHistory) && faction.lineupHistory.length > 0) ||
    updatedWrestlers.some((member) => Boolean(member?.status));

  if (!shouldPersistLineup) {
    return null;
  }

  const normalizedNow = toDate(now) || new Date();
  const timingSource = roster || faction || {};
  const mainEventerDurationDays = getMainEventerDurationDays(timingSource);
  const mainEventerCooldownDays = getMainEventerCooldownDays(timingSource);
  const previousLineup =
    normalizeLineupSnapshot(faction.currentLineup) ||
    normalizeLineupSnapshot(faction.lineup);
  const statusLineup = buildLineupFromStatuses(updatedWrestlers);

  let champion = statusLineup.champion;
  let inRing = [...statusLineup.inRing];
  let backstage = [...statusLineup.backstage];

  if (previousLineup) {
    const replacedChampion = previousLineup.champion
      ? replacements[previousLineup.champion] || previousLineup.champion
      : null;
    const replacedInRing = replaceIdsInList(previousLineup.inRing, replacements);
    const replacedBackstage = replaceIdsInList(previousLineup.backstage, replacements);
    const hasStatusAssignments =
      statusLineup.champion || statusLineup.inRing.length > 0 || statusLineup.backstage.length > 0;

    if (hasStatusAssignments) {
      inRing = replacedInRing.filter((id) => statusLineup.inRing.includes(id));
      backstage = replacedBackstage.filter(
        (id) => statusLineup.backstage.includes(id) && !inRing.includes(id)
      );

      statusLineup.inRing.forEach((id) => {
        if (!inRing.includes(id)) {
          inRing.push(id);
        }
      });
      statusLineup.backstage.forEach((id) => {
        if (!backstage.includes(id) && !inRing.includes(id)) {
          backstage.push(id);
        }
      });

      champion =
        preferPreviousChampion &&
        replacedChampion &&
        statusLineup.inRing.includes(replacedChampion)
          ? replacedChampion
          : statusLineup.champion;
    } else {
      champion = replacedChampion;
      inRing = replacedInRing;
      backstage = replacedBackstage.filter((id) => !inRing.includes(id));
    }
  }

  const previousChampion = previousLineup?.champion || null;
  const previousChampionExpired =
    previousChampion &&
    previousLineup?.mainEventerExpiresAt &&
    previousLineup.mainEventerExpiresAt.getTime() <= normalizedNow.getTime();
  let mainEventerCooldowns = normalizeMainEventerCooldowns(
    faction.mainEventerCooldowns,
    normalizedNow
  );

  if (previousChampionExpired) {
    mainEventerCooldowns = withMainEventerCooldown(
      mainEventerCooldowns,
      previousChampion,
      previousLineup.mainEventerExpiresAt,
      normalizedNow,
      mainEventerCooldownDays
    );

    if (champion === previousChampion) {
      champion = null;
    }
  }

  if (previousChampion && champion !== previousChampion && !previousChampionExpired) {
    mainEventerCooldowns = withMainEventerCooldown(
      mainEventerCooldowns,
      previousChampion,
      normalizedNow,
      normalizedNow,
      mainEventerCooldownDays
    );
  } else if (champion && mainEventerCooldowns[champion]) {
    delete mainEventerCooldowns[champion];
  }

  if (champion && !inRing.includes(champion)) {
    inRing = [champion, ...inRing.filter((id) => id !== champion)];
  }

  let mainEventerAssignedAt = null;
  let mainEventerExpiresAt = null;

  if (champion) {
    if (champion === previousChampion && previousLineup?.mainEventerAssignedAt) {
      mainEventerAssignedAt = previousLineup.mainEventerAssignedAt;
      mainEventerExpiresAt =
        previousLineup.mainEventerExpiresAt ||
        addDays(startOfDay(mainEventerAssignedAt), mainEventerDurationDays);
    } else {
      mainEventerAssignedAt = normalizedNow;
      mainEventerExpiresAt = addDays(
        startOfDay(normalizedNow),
        mainEventerDurationDays
      );
    }
  }

  const lineup = {
    champion,
    inRing,
    backstage,
  };

  const currentLineup = {
    ...lineup,
    mainEventerAssignedAt: mainEventerAssignedAt
      ? Timestamp.fromDate(mainEventerAssignedAt)
      : null,
    mainEventerExpiresAt: mainEventerExpiresAt
      ? Timestamp.fromDate(startOfDay(mainEventerExpiresAt))
      : null,
    effectiveFrom: Timestamp.fromDate(normalizedNow),
  };

  const existingHistory = Array.isArray(faction.lineupHistory)
    ? faction.lineupHistory
    : [];
  const historyWithPreviousLineup = appendUniqueLineupSnapshot(
    existingHistory,
    previousLineup
  );

  return {
    lineup,
    currentLineup,
    lineupHistory: [...historyWithPreviousLineup, currentLineup],
    mainEventerCooldowns: serializeMainEventerCooldowns(
      mainEventerCooldowns,
      normalizedNow
    ),
  };
};
